import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import type { RuntimeAnswer } from '../../types/runtime';

interface Props {
  answer: RuntimeAnswer;
}

function compactLegalPacket(packet: Record<string, unknown> | null | undefined): Record<string, unknown> | null {
  if (!packet || typeof packet !== 'object') return null;
  return {
    schema: packet.schema,
    status: packet.status,
    concept_frame_id: packet.concept_frame_id,
    concept_label: packet.concept_label,
    claim_summary: packet.claim_summary,
    key_sentence: packet.key_sentence,
    speaker: packet.speaker,
    recipient: packet.recipient,
    subject: packet.subject,
    message_date: packet.message_date,
    attachment_count: packet.attachment_count,
    source_message_id: packet.source_message_id,
    source_thread_id: packet.source_thread_id,
    attribution_status: packet.attribution_status,
  };
}

function compactGroupedPacket(packet: Record<string, unknown> | null | undefined): Record<string, unknown> | null {
  if (!packet || typeof packet !== 'object') return null;
  const buckets = Array.isArray(packet.buckets)
    ? packet.buckets
        .filter((bucket): bucket is Record<string, unknown> => Boolean(bucket && typeof bucket === 'object'))
        .slice(0, 12)
        .map((bucket) => ({
          issue_label: bucket.issue_label,
          concept_frame_id: bucket.concept_frame_id,
          status: bucket.status,
          who_said_it: bucket.who_said_it,
          claim_summary: bucket.claim_summary,
          key_sentence: bucket.key_sentence,
          speaker: bucket.speaker,
          subject: bucket.subject,
          message_date: bucket.message_date,
          attachment_count: bucket.attachment_count,
          source_message_id: bucket.source_message_id,
          source_thread_id: bucket.source_thread_id,
        }))
    : [];
  return {
    schema: packet.schema,
    status: packet.status,
    topic: packet.topic,
    matter_id: packet.matter_id,
    buckets,
  };
}

function emailSelectionMetadata(answer: RuntimeAnswer): string | undefined {
  const metadata = {
    schema: 'lex_answer_email_selection_metadata.v1',
    answer_kind: answer.kind,
    matter_id: answer.matter_id ?? null,
    email_legal_evidence_packet: compactLegalPacket(answer.email_legal_evidence_packet),
    email_topic_grouped_summary_packet: compactGroupedPacket(answer.email_topic_grouped_summary_packet),
  };
  try {
    return JSON.stringify(metadata);
  } catch {
    return undefined;
  }
}

export function EmailLinkedListCard({ answer }: Props) {
  const isGroupedSummary = answer.kind === 'email_topic_grouped_summary';
  const label = isGroupedSummary ? 'Email evidence' : 'Linked emails';

  return (
    <article
      className="rounded-lg border border-sky-200 bg-sky-50 p-4 text-sm text-sky-900"
      data-lex-selectable="true"
      data-lex-selection-source="chat_answer_email"
      data-lex-selection-label="Email answer"
      data-lex-answer-kind={answer.kind}
      data-lex-matter-id={answer.matter_id ?? undefined}
      data-lex-selection-metadata={emailSelectionMetadata(answer)}
    >
      <div className="mb-2 flex items-center gap-2">
        <span className="rounded bg-sky-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
          {label}
        </span>
        {isGroupedSummary ? <span className="text-xs text-sky-800">Grouped summary</span> : null}
        {typeof answer.email_count === 'number' && (
          <span className="text-xs text-sky-800">
            {answer.email_count} email{answer.email_count === 1 ? '' : 's'}
            {typeof answer.thread_count === 'number'
              ? ` · ${answer.thread_count} thread${answer.thread_count === 1 ? '' : 's'}`
              : ''}
          </span>
        )}
      </div>
      {isGroupedSummary ? (
        <div className="space-y-3 leading-relaxed text-sky-950">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              h1: ({ children }) => (
                <h1 className="text-base font-semibold leading-snug text-sky-950">{children}</h1>
              ),
              h2: ({ children }) => (
                <h2 className="mt-4 border-t border-sky-200 pt-3 text-sm font-semibold leading-snug text-sky-950">
                  {children}
                </h2>
              ),
              h3: ({ children }) => (
                <h3 className="mt-3 text-sm font-semibold leading-snug text-sky-900">{children}</h3>
              ),
              p: ({ children }) => <p className="leading-relaxed">{children}</p>,
              ul: ({ children }) => <ul className="ml-5 list-disc space-y-1 leading-relaxed">{children}</ul>,
              ol: ({ children }) => <ol className="ml-5 list-decimal space-y-1 leading-relaxed">{children}</ol>,
              li: ({ children }) => <li className="pl-1">{children}</li>,
              strong: ({ children }) => <strong className="font-semibold text-sky-950">{children}</strong>,
              em: ({ children }) => <em className="text-sky-900">{children}</em>,
              code: ({ children }) => (
                <code className="rounded bg-sky-100 px-1 py-0.5 text-[0.85em] text-sky-950">
                  {children}
                </code>
              ),
            }}
          >
            {answer.content}
          </ReactMarkdown>
        </div>
      ) : (
        <p className="whitespace-pre-line leading-relaxed">{answer.content}</p>
      )}
    </article>
  );
}
